import { useEffect } from 'react';
import { setViewportPinned } from '../lib/viewport';

/**
 * Lock the document to the visible viewport for a full-height chat page
 * (CLE-120).
 *
 * A chat page fits EXACTLY in the viewport: only its message list scrolls,
 * never the page. The shell (`.chat-shell` on the Layout root) owns the
 * keyboard-aware height and a flex column lays everything out — no
 * `position: fixed` anywhere, which iOS mishandles with the keyboard up. This
 * hook adds the two remaining pieces: html/body overflow is locked so the
 * document itself can never scroll, and the page opts into the gentle
 * viewport-pin (lib/viewport.ts) so any stray iOS focus-scroll is undone.
 *
 * Without it, focusing the composer on iOS pans the layout viewport up to
 * "reveal" the input and flings the header and half the composer off the top
 * of the screen. Both effects are restored on unmount so every other page
 * keeps its normal scrolling (CLE-119).
 */
export function useChatViewportLock(): void {
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const prevRootOverflow = root.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    root.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    setViewportPinned(true);

    return () => {
      setViewportPinned(false);
      root.style.overflow = prevRootOverflow;
      body.style.overflow = prevBodyOverflow;
    };
  }, []);
}
